import { useState, useEffect, useRef, useCallback } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';

/**
 * Hook to track newly deployed units and manage fade-in animations
 * Compares previous and current game state to detect new deployments
 */
/** Duration the legendary-unique deploy ray stays in DOM (matches CSS animation length) */
const LEGENDARY_UNIQUE_RAY_DURATION_MS = 4500;
/** Duration the legendary deploy glow stays (bright saturated glow behind the card) */
const LEGENDARY_GLOW_DURATION_MS = 2000;

/**
 * Per-unit deploy sound overrides.
 * Key: order id, Value: sound file under /public/sounds
 */
const UNIT_DEPLOY_SOUND_MAP = new Map([
  [1, '/sounds/hyena_howl.mp3'],
  [2, '/sounds/tumorbound.mp3'],
  [3, '/sounds/id3.mp3'],
  [4, '/sounds/id4.mp3'],
  [5, '/sounds/id5.mp3'],
  [6, '/sounds/id6.mp3'],
  [7, '/sounds/id7.mp3'],
  [8, '/sounds/id8.mp3'],
  [9, '/sounds/id9.mp3'],
  [10, '/sounds/id10.mp3'],
  [243, '/sounds/id243.mp3'],
  [239, '/sounds/id239.mp3'],
  [280, '/sounds/id280.mp3'],
  [338, '/sounds/id338.mp3'],
  [10005, '/sounds/corn.mp3'],
  [10059, '/sounds/boss_mortality.mp3'],
]);

/**
 * Additional sounds layered on top of the normal deploy sound logic.
 * Use this when a unit should keep its existing deploy sound and add another clip.
 */
const UNIT_ADDITIONAL_DEPLOY_SOUNDS = new Map([
  [262, ['/sounds/id262.mp3']],
  [386, ['/sounds/id386.mp3']],
  [378, ['/sounds/id378.mp3']],
  [381, ['/sounds/id381.mp3']],
  [384, ['/sounds/id384.mp3']],
  [385, ['/sounds/id385.mp3']],
  [382, ['/sounds/id382.mp3']],
  [387, ['/sounds/id387.mp3']],
  [389, ['/sounds/id389.mp3']],
  [391, ['/sounds/id391.mp3']],
  [392, ['/sounds/id392.mp3']],
  [393, ['/sounds/id393.mp3']],
  [394, ['/sounds/id394.mp3']],
  [395, ['/sounds/id395.mp3']],
  [396, ['/sounds/id396.mp3']],
  [397, ['/sounds/id397.mp3']],
  [377, ['/sounds/id377.mp3']],
  [390, ['/sounds/id390.mp3']],
  [402, ['/sounds/id402.mp3']],
  [403, ['/sounds/id403.mp3']],
  [500, ['/sounds/id500.mp3']],
  [501, ['/sounds/id501.mp3']]
]);

/** Slightly louder level for layered voice clips so they are audible over deploy SFX/music. */
const ADDITIONAL_DEPLOY_SOUND_VOLUME = 1.0;
/** Lower gain for unique legendary deploy SFX so unit voice lines remain clear in the mix. */
const LEGENDARY_UNIQUE_DEPLOY_SOUND_VOLUME = 0.35;
/** Default deploy SFX volume for non-unique deployments. */
const DEFAULT_DEPLOY_SOUND_VOLUME = 0.5;
/** Prevent audio spam: same unit deploy sound can only play once per window. */
const SAME_UNIT_DEPLOY_SOUND_COOLDOWN_MS = 3000;
/** Cache sound existence lookups so each file is probed at most once per session. */
const SOUND_FILE_EXISTS_CACHE = new Map();
const SOUND_FILE_EXISTS_PROMISES = new Map();
const LAST_DEPLOY_SOUND_PLAYED_AT_BY_UNIT_ID = new Map();

/** @typedef {{ timeouts: number[], audios: HTMLAudioElement[] }} DeploySoundSession */

/**
 * Stop any in-flight deploy SFX/voice for a unit instance (e.g. died before clip finished).
 * @param {Map<string, DeploySoundSession>} sessionsByInstanceId
 * @param {string} instanceId
 */
function stopDeploySoundSession(sessionsByInstanceId, instanceId) {
  const session = sessionsByInstanceId.get(instanceId);
  if (!session) return;
  session.timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
  session.audios.forEach((audio) => {
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      // ignore teardown errors
    }
  });
  sessionsByInstanceId.delete(instanceId);
}

/**
 * Stop every in-flight deploy SFX/voice (e.g. battle ends during deploy clip).
 * @param {Map<string, DeploySoundSession>} sessionsByInstanceId
 */
export function stopAllDeploySoundSessions(sessionsByInstanceId) {
  if (!sessionsByInstanceId || sessionsByInstanceId.size === 0) return;
  [...sessionsByInstanceId.keys()].forEach((instanceId) => {
    stopDeploySoundSession(sessionsByInstanceId, instanceId);
  });
}

/**
 * @param {Map<string, DeploySoundSession>} sessionsByInstanceId
 * @param {string} instanceId
 * @returns {DeploySoundSession}
 */
function getOrCreateDeploySoundSession(sessionsByInstanceId, instanceId) {
  let session = sessionsByInstanceId.get(instanceId);
  if (!session) {
    session = { timeouts: [], audios: [] };
    sessionsByInstanceId.set(instanceId, session);
  }
  return session;
}

async function doesSoundFileExist(soundPath) {
  if (!soundPath) {
    throw new Error('doesSoundFileExist: soundPath is required');
  }
  if (SOUND_FILE_EXISTS_CACHE.has(soundPath)) {
    return SOUND_FILE_EXISTS_CACHE.get(soundPath);
  }
  if (SOUND_FILE_EXISTS_PROMISES.has(soundPath)) {
    return SOUND_FILE_EXISTS_PROMISES.get(soundPath);
  }

  const checkPromise = (async () => {
    try {
      const headResponse = await fetch(soundPath, { method: 'HEAD' });
      if (headResponse.ok) {
        SOUND_FILE_EXISTS_CACHE.set(soundPath, true);
        return true;
      }
      if (headResponse.status === 405) {
        // Some static hosts block HEAD. Use GET as a compatibility check.
        const getResponse = await fetch(soundPath, { method: 'GET' });
        const exists = getResponse.ok;
        SOUND_FILE_EXISTS_CACHE.set(soundPath, exists);
        return exists;
      }
      SOUND_FILE_EXISTS_CACHE.set(soundPath, false);
      return false;
    } catch (error) {
      console.debug(`Sound existence check failed for ${soundPath}:`, error);
      SOUND_FILE_EXISTS_CACHE.set(soundPath, false);
      return false;
    } finally {
      SOUND_FILE_EXISTS_PROMISES.delete(soundPath);
    }
  })();

  SOUND_FILE_EXISTS_PROMISES.set(soundPath, checkPromise);
  return checkPromise;
}

export const useUnitDeploymentAnimations = (gameState, userId) => {
  const [newlyDeployedUnits, setNewlyDeployedUnits] = useState(new Map());
  const [legendaryUniqueRayKeys, setLegendaryUniqueRayKeys] = useState(new Set());
  const [legendaryGlowKeys, setLegendaryGlowKeys] = useState(new Set());
  const previousUnitsRef = useRef(new Map());
  const animationTimeoutRef = useRef(new Map());
  const legendaryUniqueRayTimeoutRef = useRef(new Map());
  const legendaryGlowTimeoutRef = useRef(new Map());
  const isFirstRenderRef = useRef(true);
  const mountedRef = useRef(true);
  const deploySoundSessionsByInstanceIdRef = useRef(new Map());

  const cancelAllDeploySounds = useCallback(() => {
    stopAllDeploySoundSessions(deploySoundSessionsByInstanceIdRef.current);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      deploySoundSessionsByInstanceIdRef.current.forEach((_, instanceId) => {
        stopDeploySoundSession(deploySoundSessionsByInstanceIdRef.current, instanceId);
      });
    };
  }, []);

  useEffect(() => {
    if (!gameState?.state || !userId) return;
    
    // ✅ Skip detection on first render - we don't want to play sounds for units already on the battlefield
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      // Still build the map for next comparison
      const currentUnits = new Map();
      const allPlayerIds = gameState.players || Object.keys(gameState.state);
      allPlayerIds.forEach(playerId => {
        const player = gameState.state[playerId];
        if (!player?.playedOrders) return;
        ['front', 'flank'].forEach(zone => {
          if (player.playedOrders[zone]) {
            player.playedOrders[zone].forEach((unit, idx) => {
              if (unit && unit.instanceId) {
                const key = `${playerId}_${zone}_${idx}`;
                currentUnits.set(key, unit.instanceId);
              }
            });
          }
        });
      });
      previousUnitsRef.current = currentUnits;
      return; // Don't process deployments on first render
    }

    const currentUnits = new Map();
    const allPlayerIds = gameState.players || Object.keys(gameState.state);

    // Build map of current unit positions
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (!player?.playedOrders) return;

      ['front', 'flank'].forEach(zone => {
        if (player.playedOrders[zone]) {
          player.playedOrders[zone].forEach((unit, idx) => {
            if (unit && unit.instanceId) {
              const key = `${playerId}_${zone}_${idx}`;
              currentUnits.set(key, unit.instanceId);
            }
          });
        }
      });
    });

    // Detect newly deployed units
    const newDeployments = new Map();
    const previousInstanceIdsOnBoard = new Set(previousUnitsRef.current.values());
    currentUnits.forEach((instanceId, key) => {
      const previousInstanceId = previousUnitsRef.current.get(key);
      
      // ✅ FIX: Only detect as newly deployed if:
      // 1. The slot was previously empty (previousInstanceId is undefined/null) OR had a different unit
      // 2. AND now has a unit (instanceId exists)
      // This distinguishes deployments from repositioning (where a unit moves from one slot to another)
      const wasEmpty = previousInstanceId === undefined || previousInstanceId === null;
      const isNewUnit = previousInstanceId !== instanceId;
      
      if (wasEmpty && instanceId) {
        // Same instance already occupied another slot last frame (e.g. Phase Traverse) — not a new deploy; skip sound/animation flags.
        if (previousInstanceIdsOnBoard.has(instanceId)) {
          return;
        }
        // Slot was empty and now has a unit - this is a deployment
        newDeployments.set(key, instanceId);

        // Set timeout to remove from newly deployed after animation completes.
        // Do NOT clear this timeout in effect cleanup – cleanup runs on every gameState
        // update and would cancel the timeout before it fires, so the unit would never
        // get the idle hover animation. We only clear when the unit is removed (below).
        const timeoutId = setTimeout(() => {
          if (!mountedRef.current) return;
          setNewlyDeployedUnits(prev => {
            const next = new Map(prev);
            next.delete(key);
            return next;
          });
          animationTimeoutRef.current.delete(key);
        }, 600); // Animation duration + small buffer
        
        animationTimeoutRef.current.set(key, timeoutId);
      } else if (isNewUnit && instanceId && !wasEmpty) {
        // Slot changed from one unit instance to another.
        // Treat as deployment ONLY when the previous instance is no longer anywhere on the board:
        // - replacement deploy (6th unit replacing existing) -> previous instance disappears => play deploy sound
        // - repositioning -> previous instance still exists in another slot => do not play deploy sound
        const previousStillOnBoard = Array.from(currentUnits.values()).includes(previousInstanceId);
        if (!previousStillOnBoard) {
          newDeployments.set(key, instanceId);

          const timeoutId = setTimeout(() => {
            if (!mountedRef.current) return;
            setNewlyDeployedUnits(prev => {
              const next = new Map(prev);
              next.delete(key);
              return next;
            });
            animationTimeoutRef.current.delete(key);
          }, 600);

          animationTimeoutRef.current.set(key, timeoutId);
        }
      }
    });

    // Stop deploy SFX/voice when a unit leaves the board (death, decommission, etc.)
    const currentInstanceIdsOnBoard = new Set(currentUnits.values());
    deploySoundSessionsByInstanceIdRef.current.forEach((_, instanceId) => {
      if (!currentInstanceIdsOnBoard.has(instanceId)) {
        stopDeploySoundSession(deploySoundSessionsByInstanceIdRef.current, instanceId);
      }
    });

    // Clean up units that were removed
    previousUnitsRef.current.forEach((instanceId, key) => {
      if (!currentUnits.has(key)) {
        // Unit was removed, clear any pending timeout
        const timeoutId = animationTimeoutRef.current.get(key);
        if (timeoutId) {
          clearTimeout(timeoutId);
          animationTimeoutRef.current.delete(key);
        }
        const rayTimeoutId = legendaryUniqueRayTimeoutRef.current.get(key);
        if (rayTimeoutId) {
          clearTimeout(rayTimeoutId);
          legendaryUniqueRayTimeoutRef.current.delete(key);
        }
        const glowTimeoutId = legendaryGlowTimeoutRef.current.get(key);
        if (glowTimeoutId) {
          clearTimeout(glowTimeoutId);
          legendaryGlowTimeoutRef.current.delete(key);
        }
        setLegendaryUniqueRayKeys(prev => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        setLegendaryGlowKeys(prev => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    });

    // Update newly deployed units
    if (newDeployments.size > 0) {
      setNewlyDeployedUnits(prev => {
        const next = new Map(prev);
        newDeployments.forEach((instanceId, key) => {
          next.set(key, instanceId);
        });
        return next;
      });

      // ✅ Skip deploy sounds only when this looks like page load/reconnect: previous snapshot
      // was empty AND we're seeing many new units at once. First deployment in a game has
      // previous empty but newDeployments.size === 1, so we still play that sound.
      const isReconnectOrRefresh =
        previousUnitsRef.current.size === 0 && newDeployments.size >= 2;

      if (!isReconnectOrRefresh) {
      // ✅ Play deploy sound for each newly deployed unit
      // Use a small delay to batch multiple deployments and avoid sound overlap
      newDeployments.forEach((instanceId, key) => {
        // Key format: `${playerId}_${zone}_${idx}` – playerId may contain underscores (e.g. "player_1")
        const parts = key.split('_');
        const indexStr = parts.pop();
        const zone = parts.pop();
        const playerId = parts.join('_');
        const index = parseInt(indexStr, 10);
        const player = gameState.state[playerId];

        if (player?.playedOrders?.[zone]?.[index]) {
          const order = player.playedOrders[zone][index];
          
          // ✅ Play sound for all deployments except strategems, weapons, and drones
          // Only units can be deployed to zones, so we just exclude the other types
          const isExcludedType = order && (
            order.type === 'Strategem' || 
            order.type === 'Weapon' || 
            order.type === 'Drone' ||
            order.type === 'Stratagem' || // Handle typo variant
            order.isExploreUnknownMemory === true
          );
          
          if (!isExcludedType) {
            // Choose sound by rarity: legendary + is_unique → legendary_deploy.mp3, legendary → legendary_deploy_4.mp3, else deploy.mp3
            // Support both camelCase and snake_case (backend may send either)
            const rarity = (order.rarity ?? '').toString().toLowerCase();
            const isUnique = order.is_unique === true || order.isUnique === true;
            const availability = (order.availability ?? '').toString().trim().toLowerCase();
            const isAvailabilityCreated = availability === 'created';
            const isLegendary = rarity === 'legendary';
            // Units with availability=created (e.g. Shadow Sniper) use standard legendary animation/sound, not unique legendary.
            const isLegendaryUnique = isLegendary && isUnique && !isAvailabilityCreated;
            // Legendary glow: bright saturated glow for 2s – for legendary units that are NOT unique (e.g. t-rex), or availability=created (e.g. Shadow Sniper). Legendary-unique (e.g. Samuel Johnson) get the golden ray only, no glow.
            if (isLegendary && (!isUnique || isAvailabilityCreated)) {
              setLegendaryGlowKeys(prev => new Set(prev).add(key));
              const existingGlowTimeout = legendaryGlowTimeoutRef.current.get(key);
              if (existingGlowTimeout) clearTimeout(existingGlowTimeout);
              const glowTimeoutId = setTimeout(() => {
                if (!mountedRef.current) return;
                legendaryGlowTimeoutRef.current.delete(key);
                setLegendaryGlowKeys(prev => {
                  if (!prev.has(key)) return prev;
                  const next = new Set(prev);
                  next.delete(key);
                  return next;
                });
              }, LEGENDARY_GLOW_DURATION_MS);
              legendaryGlowTimeoutRef.current.set(key, glowTimeoutId);
            }
            // Keep legendary-unique ray in DOM for full animation (4.5s); general "newly deployed" clears at 600ms
            if (isLegendaryUnique) {
              setLegendaryUniqueRayKeys(prev => new Set(prev).add(key));
              const existingRayTimeout = legendaryUniqueRayTimeoutRef.current.get(key);
              if (existingRayTimeout) clearTimeout(existingRayTimeout);
              const rayTimeoutId = setTimeout(() => {
                if (!mountedRef.current) return;
                legendaryUniqueRayTimeoutRef.current.delete(key);
                setLegendaryUniqueRayKeys(prev => {
                  if (!prev.has(key)) return prev;
                  const next = new Set(prev);
                  next.delete(key);
                  return next;
                });
              }, LEGENDARY_UNIQUE_RAY_DURATION_MS);
              legendaryUniqueRayTimeoutRef.current.set(key, rayTimeoutId);
            }
            const unitId = Number(order.id);
            const unitSpecificDeploySound = UNIT_DEPLOY_SOUND_MAP.get(unitId);
            const deploySoundPath = unitSpecificDeploySound || (isLegendaryUnique
              ? '/sounds/legendary_deploy.mp3'
              : isLegendary
                ? '/sounds/legendary_deploy_4.mp3'
                : '/sounds/deploy.mp3');
            const additionalDeploySounds = UNIT_ADDITIONAL_DEPLOY_SOUNDS.get(unitId) || [];
            const deploySoundVolume = isLegendaryUnique
              ? LEGENDARY_UNIQUE_DEPLOY_SOUND_VOLUME
              : DEFAULT_DEPLOY_SOUND_VOLUME;
            const autoVoiceSoundPath = `/sounds/id${unitId}.mp3`;
            // Small delay to stagger sounds if multiple units deploy at once
            const delay = newDeployments.size > 1 ? Array.from(newDeployments.keys()).indexOf(key) * 100 : 0;
            const deploySoundSession = getOrCreateDeploySoundSession(
              deploySoundSessionsByInstanceIdRef.current,
              instanceId
            );
            const scheduleDeploySoundTimeoutId = setTimeout(async () => {
              if (!deploySoundSessionsByInstanceIdRef.current.has(instanceId)) {
                return;
              }
              const now = Date.now();
              const lastPlayedAt = LAST_DEPLOY_SOUND_PLAYED_AT_BY_UNIT_ID.get(unitId) || 0;
              if (now - lastPlayedAt < SAME_UNIT_DEPLOY_SOUND_COOLDOWN_MS) {
                console.log(`🔇 Skipping deploy sound for unit ${unitId}: cooldown active (${now - lastPlayedAt}ms < ${SAME_UNIT_DEPLOY_SOUND_COOLDOWN_MS}ms)`);
                stopDeploySoundSession(deploySoundSessionsByInstanceIdRef.current, instanceId);
                return;
              }

              console.log(`🔊 Playing deploy sound for ${order.name || 'unit'} (ID: ${order.id}) at ${zone}[${index}] -> ${deploySoundPath}`);
              try {
                const mainAudio = playSoundEffect(deploySoundPath, deploySoundVolume);
                if (mainAudio) {
                  deploySoundSession.audios.push(mainAudio);
                }
                const manualAdditionalSounds = [...additionalDeploySounds];
                const autoVoiceExists = await doesSoundFileExist(autoVoiceSoundPath);
                if (autoVoiceExists && !manualAdditionalSounds.includes(autoVoiceSoundPath)) {
                  manualAdditionalSounds.push(autoVoiceSoundPath);
                }
                manualAdditionalSounds.forEach((extraSoundPath, extraIdx) => {
                  // Tiny stagger so layered clips do not start on the same exact frame.
                  const extraTimeoutId = setTimeout(() => {
                    if (!deploySoundSessionsByInstanceIdRef.current.has(instanceId)) {
                      return;
                    }
                    const extraAudio = playSoundEffect(extraSoundPath, ADDITIONAL_DEPLOY_SOUND_VOLUME);
                    if (extraAudio) {
                      deploySoundSession.audios.push(extraAudio);
                    }
                  }, extraIdx * 40);
                  deploySoundSession.timeouts.push(extraTimeoutId);
                });
                LAST_DEPLOY_SOUND_PLAYED_AT_BY_UNIT_ID.set(unitId, now);
                console.log(`✅ Deploy sound triggered successfully`);
              } catch (error) {
                console.error(`❌ Error playing deploy sound:`, error);
              }
            }, delay);
            deploySoundSession.timeouts.push(scheduleDeploySoundTimeoutId);
          } else {
            console.log(`⚠️ Skipping deploy sound: order type is ${order?.type} (excluded type)`);
          }
        } else {
          console.warn(`⚠️ DEPLOY SOUND: Order not found at ${zone}[${index}] for player ${playerId}`);
        }
      });
      }

    }

    // Update previous units reference
    previousUnitsRef.current = currentUnits;

    // Only clear timeouts for units that left the slot (handled above). Do NOT clear
    // all timeouts here – that runs on every gameState update and prevented the 600ms
    // timeout from ever firing, so newly deployed units never got the idle hover class.
  }, [gameState, userId]);

  /**
   * Check if a unit at a specific position is newly deployed
   */
  const isNewlyDeployed = (playerId, zone, index) => {
    const key = `${playerId}_${zone}_${index}`;
    return newlyDeployedUnits.has(key);
  };

  /**
   * Check if the golden ray VFX should show for this slot (legendary unique deploy).
   * Uses a separate 4.5s duration so the ray stays visible for the full CSS animation.
   */
  const isNewlyDeployedLegendaryUnique = (playerId, zone, index) => {
    const key = `${playerId}_${zone}_${index}`;
    return legendaryUniqueRayKeys.has(key);
  };

  /**
   * Check if the bright saturated glow should show behind this slot (legendary deploy, e.g. t-rex).
   * Lasts 2 seconds.
   */
  const isNewlyDeployedLegendary = (playerId, zone, index) => {
    const key = `${playerId}_${zone}_${index}`;
    return legendaryGlowKeys.has(key);
  };

  /**
   * Get fade-in animation styles for a newly deployed unit
   * Note: With flexbox layout, units are automatically positioned correctly
   * This animation only handles the visual fade-in effect
   * The animation uses transform: scale() which will combine with existing transforms
   */
  const getDeploymentAnimationStyles = (playerId, zone, index) => {
    if (isNewlyDeployed(playerId, zone, index)) {
      return {
        animation: 'fadeInDeploy 0.5s ease-in-out',
        animationFillMode: 'forwards',
      };
    }
    return {};
  };

  return {
    isNewlyDeployed,
    isNewlyDeployedLegendaryUnique,
    isNewlyDeployedLegendary,
    getDeploymentAnimationStyles,
    cancelAllDeploySounds,
  };
};

